import { useLayoutEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { IconType } from "~/common/components/icon/IconType";
import Modal, { ModalSize } from "~/common/components/Modal";
import { type QuickEntryForm, type QuickEntryItem } from "../types/models";
import { QuickEntryTable } from "./QuickEntryTable";
import { addProductsToCartAction } from "../redux/quickEntryActions";
import { useDispatch } from "react-redux";
import { hasProductConfigured } from "../utils/helpers";
import { addMessage } from "~/features/toasts/redux/actions";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import CenterContents from "~/common/components/CenterContents";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { Button, Stack } from "~/common/mui";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";

interface Props {
  onClose: () => void;
}

export const QuickEntryPopup = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const formProps = useForm<QuickEntryForm>({
    mode: "onBlur",
    defaultValues: { items: [] },
    shouldUnregister: false,
  });

  useLayoutEffect(() => {
    formProps.register("items");
  }, []);

  const [isSending, setSending] = useState(false);

  const formRef = useRef<HTMLButtonElement>(null);

  const onSubmitClick = () => {
    return formRef.current?.click();
  };

  const numberItems = formProps.watch().items?.filter(hasProductConfigured).length ?? 0;

  const dispatch = useDispatch();
  const onSubmit = async (data: QuickEntryForm) => {
    const items = data.items?.filter(hasProductConfigured) ?? [];
    const modifiedItems = items.map((item: QuickEntryItem) => {
      const filteredProductId = item.productId.replace(/-/g, "");
      const isMatNo = !!parseInt(filteredProductId) && filteredProductId.length == 15;

      return {
        ...item,
        productId: isMatNo ? filteredProductId : item.productId,
        packCode: isMatNo ? filteredProductId.substring(13, 15) : item.packCode,
      };
    });
    setSending(true);
    await dispatch(addProductsToCartAction(modifiedItems));
    setSending(false);
    if (numberItems > 0) {
      dispatch(addMessage("info", t("addToCart.success", { count: numberItems })));
    }
    onClose();
  };

  const isAddToCartDisabled = window.SCHA.config.disableAllWS || numberItems === 0;

  const isTablet = useIsMinTabletSize();

  return (
    <Modal
      modalClassName={classNames("scha__modal__quick-entry")}
      size={ModalSize.LARGE}
      title={t("modal.quickEntry.title")}
      onClose={onClose}
      footerChildren={
        <Stack direction="row" gap={1}>
          <Button color={"primary"} variant={"text"} onClick={onClose} data-test-id={"btn-quick-entry-cancel"}>
            {t("cancel")}
          </Button>
          <Button
            startIcon={<DefaultButtonIcon type={IconType.Cart} />}
            onClick={onSubmitClick}
            disabled={isAddToCartDisabled}
            data-test-id={"btn-quick-entry-submit"}
          >
            {t("addToCartPopup.add.submitBtn")}
          </Button>
        </Stack>
      }
    >
      <FormProvider {...formProps}>
        <form onSubmit={formProps.handleSubmit(onSubmit)} className={isSending ? "v-hidden" : undefined}>
          <button ref={formRef} type={"submit"} hidden />
          <QuickEntryTable />
        </form>

        {isSending && (
          <CenterContents className={"scha__quick-entry__spinner"}>
            <CircleSpinner />
          </CenterContents>
        )}
      </FormProvider>
    </Modal>
  );
};
